// MQTT WebSocket client for real-time event notifications

"use strict";

import mqtt from 'mqtt';
import { AuthController } from '@/control/auth';
import { EventData } from '@/api/definitions';

export interface MQTTClientConfig {
    onEventReceived?: (event: EventData) => void;
    onConnectionChange?: (connected: boolean) => void;
    onError?: (error: any) => void;
}

export class MQTTEventClient {
    private client: mqtt.MqttClient | null = null;
    private config: MQTTClientConfig;
    private isConnected = false;
    private reconnectAttempts = 0;
    private maxReconnectAttempts = Number(import.meta.env.VITE__MQTT_MAX_RECONNECT_ATTEMPTS) || 5;
    private reconnectInterval = Number(import.meta.env.VITE__MQTT_RECONNECT_PERIOD) || 5000;

    constructor(config: MQTTClientConfig = {}) {
        this.config = {
            onEventReceived: config.onEventReceived || (() => {}),
            onConnectionChange: config.onConnectionChange || (() => {}),
            onError: config.onError || (() => {}),
        };
    }

    /**
     * Conecta al broker MQTT via WebSocket
     */
    public connect(): void {
        if (this.client) {
            console.warn('MQTT client already connected');
            return;
        }

        if (!AuthController.isAuthenticated()) {
            console.error('Cannot connect MQTT: user not authenticated');
            return;
        }

        const mqttHost = import.meta.env.VITE__MQTT_WS_HOST || 'localhost';
        const mqttPort = import.meta.env.VITE__MQTT_WS_PORT || '9001';
        const mqttSecure = import.meta.env.VITE__MQTT_WS_SECURE === 'NO' ? false : true;
        const brokerUrl = `${mqttSecure ? 'wss' : 'ws'}://${mqttHost}:${mqttPort}`;

        try {
            this.client = mqtt.connect(brokerUrl, {
                clientId: `frontend_${AuthController.UID}_${Date.now()}`,
                keepalive: Number(import.meta.env.VITE__MQTT_KEEPALIVE) || 60,
                clean: true,
                connectTimeout: Number(import.meta.env.VITE__MQTT_CONNECT_TIMEOUT) || 30000,
                reconnectPeriod: this.reconnectInterval,
            });

            this.setupEventHandlers();
        } catch (error) {
            console.error('Error creating MQTT client:', error);
            this.config.onError(error);
        }
    }

    /**
     * Desconecta del broker MQTT
     */
    public disconnect(): void {
        if (this.client) {
            this.client.end();
            this.client = null;
            this.isConnected = false;
            this.config.onConnectionChange(false);
        }
    }

    /**
     * Verifica si está conectado
     */
    public isConnectedToMQTT(): boolean {
        return this.isConnected;
    }

    /**
     * Configura los event handlers del cliente MQTT
     */
    private setupEventHandlers(): void {
        if (!this.client) return;

        this.client.on('connect', () => {
            this.isConnected = true;
            this.reconnectAttempts = 0;
            this.config.onConnectionChange(true);
            this.subscribeToUserEvents();
        });

        this.client.on('message', (topic: string, payload: Buffer) => {
            try {
                const message = payload.toString();

                // Parse el evento recibido
                const event: EventData = JSON.parse(message);
                
                // Procesar todos los tipos de eventos
                this.config.onEventReceived(event);
            } catch (error) {
                console.error('Error parsing MQTT message:', error);
                this.config.onError(error);
            }
        });

        this.client.on('error', (error) => {
            console.error('MQTT client error:', error);
            this.isConnected = false;
            this.config.onConnectionChange(false);
            this.config.onError(error);
        });

        this.client.on('offline', () => {
            this.isConnected = false;
            this.config.onConnectionChange(false);
        });

        this.client.on('reconnect', () => {
            this.reconnectAttempts++;
            if (this.reconnectAttempts >= this.maxReconnectAttempts) {
                console.error('Max MQTT reconnection attempts reached');
                this.disconnect();
            }
        });

        this.client.on('close', () => {
            //console.log('MQTT connection closed');
            this.isConnected = false;
            this.config.onConnectionChange(false);
        });
    }

    /**
     * Se suscribe al topic de eventos del usuario autenticado
     */
    private subscribeToUserEvents(): void {
        if (!this.client || !this.isConnected) {
            console.error('Cannot subscribe: MQTT client not connected');
            return;
        }

        const userID = AuthController.UID;
        if (!userID) {
            console.error('Cannot subscribe: user ID not available');
            return;
        }

        const topic = `users/${userID}/event`;
        
        //console.log('Subscribing to MQTT topic:', topic);

        this.client.subscribe(topic, { qos: 1 }, (error) => {
            if (error) {
                console.error('MQTT subscription error:', error);
                this.config.onError(error);
            }
        });
    }

    /**
     * Actualiza la configuración del cliente
     */
    public updateConfig(newConfig: Partial<MQTTClientConfig>): void {
        this.config = { ...this.config, ...newConfig };
    }
}

// Instancia global singleton
let globalMQTTClient: MQTTEventClient | null = null;

/**
 * Obtiene o crea el cliente MQTT global
 */
export function getMQTTEventClient(config?: MQTTClientConfig): MQTTEventClient {
    if (!globalMQTTClient) {
        globalMQTTClient = new MQTTEventClient(config);
    } else if (config) {
        globalMQTTClient.updateConfig(config);
    }
    
    return globalMQTTClient;
}

/**
 * Destruye el cliente MQTT global
 */
export function destroyMQTTEventClient(): void {
    if (globalMQTTClient) {
        globalMQTTClient.disconnect();
        globalMQTTClient = null;
    }
}